import {Component, inject, OnInit, ViewChild} from '@angular/core';
import {TransactionBooking} from '../../model/transaction-booking';
import {TransactionBookingViewerModalComponent,TransactionBookingViewerType} from '../viewer/transaction-booking-viewer-modal/transaction-booking-viewer-modal.component';
import {AutoUnsubscribe} from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import {BookingTemplate} from 'src/app/booking-template/model/booking-template';
import {AzureAuthService} from 'src/infrastructure/services/azure-auth.service';
import {VendorViewerModalComponent, VendorViewerType} from 'src/app/vendor/component/viewer/vendor-viewer-modal/vendor-viewer-modal.component';
import {of, switchMap} from 'rxjs';
import {ModalService} from 'src/app/shared/service/modal/modal.service';
import {TaskService} from 'src/app/task/service/task.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {NotificationService} from 'src/app/notification/service/notification.service';
import {GridEventOutput, GridModel} from 'src/app/shared/model/page-grid';
import {TransactionBookingGridService} from '../../service/transaction-booking-grid.service';
import {DatePipe} from '@angular/common';
import {BookingStatusService} from 'src/app/booking-status/service/booking-status.service';
import {ServiceStatusService} from 'src/app/service-status/service/service-status.service';
import {PageGridComponent} from 'src/app/shared/components/page-grid/page-grid.component';

@Component({
    selector: 'transaction-booking-list',
    templateUrl: './transaction-booking-list.component.html',
    standalone: false
})
@AutoUnsubscribe()
export class TransactionBookingListComponent implements OnInit {

  @ViewChild(PageGridComponent) pageGridComponentRef!: PageGridComponent;

  private azureAuthService = inject(AzureAuthService);
  private modalService = inject(ModalService);
  private taskService = inject(TaskService);
  private snackBar = inject(MatSnackBar);
  private notificationService = inject(NotificationService);
  private datePipe = inject(DatePipe);
  private bookingStatusService = inject(BookingStatusService);
  private serviceStatusService = inject(ServiceStatusService);

  private bookingViewerType = TransactionBookingViewerType;

  protected isLoggedInUserHasManagerRole: boolean = false;

  protected readonly servicesGrid: GridModel<any> = {
    gridDataTestId: 'booking-trip',
    gridTitle: 'Services',
    replayButton: true,
    defaultSort: {active: 'dateCreated', direction: 'desc'},
    columns: [
      {
        columnProperty: 'tripReference',
        columnHeader: 'Trip Reference',
        sort: true,
        clickable: true,
      },
      {
        columnProperty: 'Agent.FirstName',
        columnHeader: 'Agent Name',
        sort: true,
        textLeft: true,
        columnPropertyFunction: (item:any)=> item.agentName,
      },
      {
        columnProperty: 'bookingName',
        columnHeader: 'Name',
        sort: true,
        textLeft: true,
      },
      {
        columnProperty: 'bookingStartDate',
        columnHeader: 'Start Date',
        sort: true,
        processData: (date:string)=> this.datePipe.transform(date, 'M/d/yyyy') ?? '',
      },
      {
        columnProperty: 'bookingEndDate',
        columnHeader: 'End Date',
        sort: true,
        processData: (date:string)=> this.datePipe.transform(date, 'M/d/yyyy') ?? '',
      },
      {
        columnProperty: 'dateCreated',
        columnHeader: 'Date Created',
        sort: true,
        processData: (date:string)=> this.datePipe.transform(date, 'M/d/yyyy') ?? '',
      },
      {
        columnProperty: 'serviceStatus.Name',
        columnHeader: 'Status',
        sort: true,
        textLeft: true,
        columnPropertyFunction: (item:any) => item?.serviceStatusName,
      },
      {
        columnProperty: 'BookingStatus.BookingStatusName',
        columnHeader: 'FMS Status',
        sort: true,
        columnPropertyFunction: (item:any) => item?.bookingStatusName,
      },
      {
        columnProperty: 'vendorPaymentTC',
        columnHeader: 'Service Payment TC',
        sort: false,
        columnIconDisplay: 'checkmark',
        shouldHideCellContent: (item:any)=> this.checkVendorPaymentBookingTemplate(item.bookingTemplates),
      },
      {
        columnProperty: 'cancellationRefundTC',
        columnHeader: 'Service Cancellation Refund TC',
        sort: false,
        columnIconDisplay: 'checkmark',
        shouldHideCellContent: (item:any)=> this.checkCancellationRefundBookingTemplate(item.bookingTemplates),
      },
      {
        columnProperty: 'Vendor.VendorName',
        columnHeader: 'Vendor Name',
        textLeft: true,
        columnPropertyFunction: (item:any)=> item.vendor?.vendorName,
        sort: true,
      },      
      {
        columnProperty: 'vendorId',
        columnHeader: 'Vendor Details',
        columnIconDisplay: 'storefront',
        shouldHideCellContent: (item:any)=> this.checkVendorInformation(item.vendorId),
        sort: true,
      },
      {
        columnProperty: 'commissionTotal',
        columnHeader: 'Commission',
        sort: true,
        columnPropertyFunction: (item:any)=> item?.commissionTotal || 0,
      },
    ],
    filters: [
      {
        filterProperty: 'bookingStatusName',
        filterDisplayName: 'FMS Statuses',
        filterType: 'DROPDOWN',
        config: {
          dropdownOptions: this.bookingStatusService.getBookingStatuses({useCache: true, slidingExpiration: 1, cacheKey: 'BOOKING_STATUSES'}),
        }
      },
      {
        filterProperty: 'serviceStatusName',
        filterDisplayName: 'Service Statuses',
        filterType: 'DROPDOWN',
        config: {
          dropdownOptions: this.serviceStatusService.getServiceStatuses({useCache: true, slidingExpiration: 1, cacheKey: 'SERVICE_STATUSES'}),
        }
      },
      {
        filterProperty: 'bookingStartFrom',
        filterDisplayName: 'Service Start',
        filterType: 'DATERANGE',
        lastFilterProperty: 'bookingStartTo',
      },
      {
        filterProperty: 'bookingEndFrom',
        filterDisplayName: 'Service End',
        filterType: 'DATERANGE',
        lastFilterProperty: 'bookingEndTo',
      },
      {
        filterProperty: 'dateCreatedFrom',
        filterDisplayName: 'Date Created',
        filterType: 'DATERANGE',
        lastFilterProperty: 'dateCreatedTo',
      },
      {
        filterProperty: 'agentName',
        filterDisplayName: 'Agent Name',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'agent',
          autocompleteColumnName: 'agentName',
          autocompleteEmitText: true
        }
      },
      {
        filterProperty: 'bookingName',
        filterDisplayName: 'Booking Name',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'transaction_booking',
          autocompleteColumnName: 'bookingName',
          autocompleteEmitText: true
        }
      },
      {
        filterProperty: 'externalNumber',
        filterDisplayName: 'Helpspot Ticket No.',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'transaction_booking',
          autocompleteColumnName: 'externalNumber',
          autocompleteEmitText: true
        }
      },
      {
        filterProperty: 'tripReference',
        filterDisplayName: 'Trip Reference No.',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'transaction_booking',
          autocompleteColumnName: 'tripReference',
          autocompleteEmitText: true
        }
      },
    ],
    gridGetService: inject(TransactionBookingGridService)
  };

  ngOnInit(): void {
    this.isLoggedInUserHasManagerRole = this.azureAuthService.checkUserAzureTokenHasManagerRole();

    this.servicesGrid.showActionColumn = this.isLoggedInUserHasManagerRole;
  }

  protected openDialog({item, column}: GridEventOutput<any>): void {
    if(column === 'vendorPaymentTC') {
      this.openModal(item, this.bookingViewerType.VendorPayment);
      return;
    }

    if(column === 'cancellationRefundTC') {
      this.openModal(item, this.bookingViewerType.CancellationRefund);
      return;
    }

    if(column === 'vendorId') {
      this.openModal(item, this.bookingViewerType.VendorInformation);
      return;
    }
    
    this.openModal(item, this.bookingViewerType.Booking);
  } 

  protected openModal(record: TransactionBooking, viewerType: TransactionBookingViewerType): void {
    let title;

    if (viewerType == TransactionBookingViewerType.VendorPayment) {
      title = 'Vendor Payment Terms and Conditions';
    } else if (viewerType == TransactionBookingViewerType.CancellationRefund) {
      title = 'Cancellation Refund Terms and Conditions';
    } else if (viewerType == TransactionBookingViewerType.VendorInformation) {
      title ='Vendor Information';
    }else {
      title = 'Service Information';
    }
    if(title == 'Vendor Information') {
      const vendorViewerRef = this.modalService.open(VendorViewerModalComponent, { width: '55%' });
      const vendorViewerInstance = vendorViewerRef.componentInstance as VendorViewerModalComponent;
      vendorViewerInstance.title = title;
      vendorViewerInstance.viewerType = VendorViewerType.Vendor;
      vendorViewerInstance.vendorData = record.vendor;
    }
    else{
        const bookingViewerRef = this.modalService.open(TransactionBookingViewerModalComponent, { width: '55%' });
        const bookingViewerInstance = bookingViewerRef.componentInstance as TransactionBookingViewerModalComponent;
        bookingViewerInstance.title = title;
        bookingViewerInstance.viewerType = viewerType;
        bookingViewerInstance.bookingData = record;
    }
  }

  protected checkVendorInformation(vendorId: number): boolean {
    return vendorId > 0;
  }
  
  protected checkVendorPaymentBookingTemplate(bookingTemplates: BookingTemplate[]): boolean {
    return bookingTemplates?.some(x => !x.isDeleted && x.termsConditionsTypeId == Number(TransactionBookingViewerType.VendorPayment));
  }

  protected checkCancellationRefundBookingTemplate(bookingTemplates: BookingTemplate[]): boolean {
    return bookingTemplates?.some(x => !x.isDeleted && x.termsConditionsTypeId == Number(TransactionBookingViewerType.CancellationRefund));
  }

  protected upsertHsTicket(row: TransactionBooking, action: string): void {
    if (action == "create") {
      this.taskService
        .createTask({
          transactionBookingId: row.transactionBookingId,
          taskDescription: 'booking ' + row.transactionBookingId + ' to helpspot',
          taskName: 'Create HS Ticket for Booking from ADX, trip reference: ' + row.tripReference,
          taskStatusId: 1,
          taskTypeId: 1,
        }).subscribe((response: any) => {
          if (response) {
            this.pageGridComponentRef.getItems();
            const message = "Successfully helpspot ticket is created for the booking name '" + row.bookingName + "'!!";
            this.snackBar.open(message, 'Dismiss', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['message-position', 'app-notification-success']
            });

            this.notificationService.getNotification(row.transactionBookingId).pipe(
              switchMap((notification: any) => {
                if (notification && Object.keys(notification).length > 0) {
                  const notificationId = notification?.id;
                  if (notificationId) {
                    return this.notificationService.updateNotification(notificationId, true);
                  }
                }
                return of(notification);
              })
            ).subscribe(() => {
            });
          }
        });
    } else {
      const externalNumbers: number[] = row.tasks.map(task => parseInt(task.externalNumber));
      const totalExternalNumbers = externalNumbers.length;
      externalNumbers.forEach((externalNumber, index) => {
        this.taskService
          .updateHelpSpot({
            ticketNumber: externalNumber
          }).subscribe((result: boolean) => {
            const isLastIteration = index === totalExternalNumbers - 1;
            if (result && isLastIteration) {
              this.pageGridComponentRef.getItems();
              const message = "Successfully helpspot ticket is updated with the ticket Id's: " +
                externalNumbers.join(',') + " for the booking name '" + row.bookingName + "'!!";
              this.snackBar.open(message, 'Dismiss', {
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['message-position', 'app-notification-success']
              });
            }
          });
      });
    }
  }
}
